/**
 * Workflow Status Component
 * 
 * Displays workflow state, validation status, and next actions for prime artifacts
 * Integrated into the CompositeCard system
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, List, Progress, Tag, Spin, message, Alert, Steps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { apiClient } from '../../../services/apiClient';
import { CardSectionProps } from '../../../types/compositeCards';

interface WorkflowValidation {
  is_valid: boolean;
  current_state: string;
  next_state: string | null;
  can_advance: boolean;
  errors: string[];
  warnings: string[];
  required_actions: string[];
  completion_percentage: number;
}

interface ApprovalStatus {
  details_approved: boolean;
  states_approved: boolean;
  transitions_approved: boolean;
  processes_approved: boolean;
}

const WORKFLOW_STATE_CONFIG: Record<string, { color: string; badge: any; label: string }> = {
  IDENTIFIED: { color: '#d9d9d9', badge: 'default', label: 'Identified' },
  DEFINED: { color: '#1890ff', badge: 'processing', label: 'Defined' },
  DESIGNED: { color: '#722ed1', badge: 'processing', label: 'Designed' },
  IMPLEMENTED: { color: '#faad14', badge: 'warning', label: 'Implemented' },
  VALIDATED: { color: '#52c41a', badge: 'success', label: 'Validated' }
};

const WORKFLOW_STATE_ORDER = ['IDENTIFIED', 'DEFINED', 'DESIGNED', 'IMPLEMENTED', 'VALIDATED'];

export const WorkflowStatusComponent: React.FC<CardSectionProps> = ({ card, onAction }) => {
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState<WorkflowValidation | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(null);

  // Extract artifact ID and artifact data from card data - handle different card data structures
  const artifactId = 
    (card.data as any).artifact?.id || 
    (card.data as any).artifactId ||
    ((card.data as any).items && (card.data as any).items[0]?.id);
  
  const artifact = (card.data as any).artifact;

  useEffect(() => {
    if (artifactId) {
      loadValidation();
      loadApprovalStatus();
    }
  }, [artifactId, artifact]);

  const loadValidation = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/prime-artifacts/${artifactId}/workflow-validation`);
      setValidation(response.data);
    } catch (error: any) {
      // Handle 404 gracefully - endpoint may not exist yet
      if (error.response?.status === 404) {
        // Silently handle 404 - endpoint may not exist yet
        setValidation(null);
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading workflow validation:', error);
        }
        // Only show error message for non-404 errors
        message.error(`Failed to load workflow status: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadApprovalStatus = () => {
    // Extract approval status from artifact data if available
    if (artifact) {
      setApprovalStatus({
        details_approved: artifact.details_approved || false,
        states_approved: artifact.states_approved || false,
        transitions_approved: artifact.transitions_approved || false,
        processes_approved: artifact.processes_approved || false,
      });
    } else {
      // Try to fetch artifact data if not available
      if (artifactId) {
        apiClient.get(`/prime-artifacts/${artifactId}`)
          .then(response => {
            const artifactData = response.data;
            setApprovalStatus({
              details_approved: artifactData.details_approved || false,
              states_approved: artifactData.states_approved || false,
              transitions_approved: artifactData.transitions_approved || false,
              processes_approved: artifactData.processes_approved || false,
            });
          })
          .catch(error => {
            console.error('Error loading approval status:', error);
          });
      }
    }
  };

  const handleAdvanceWorkflow = async () => {
    if (!validation?.next_state) return;

    try {
      setAdvancing(true);
      await apiClient.put(`/prime-artifacts/${artifactId}/workflow-transition`, {
        to_state: validation.next_state
      });
      message.success(`Artifact advanced to ${validation.next_state}`);
      
      // Reload validation
      await loadValidation();
      
      // Notify parent
      if (onAction) {
        onAction('workflow-advanced', { artifactId, newState: validation.next_state });
      }
    } catch (error: any) {
      message.error(`Failed to advance workflow: ${error.response?.data?.detail || error.message}`);
      console.error('Error advancing workflow:', error);
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <Card size="small">
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin />
        </div>
      </Card>
    );
  }

  if (!validation) {
    return (
      <Card size="small">
        <Alert message="Workflow status unavailable" type="warning" />
      </Card>
    );
  }

  const stateConfig = WORKFLOW_STATE_CONFIG[validation.current_state] || WORKFLOW_STATE_CONFIG.IDENTIFIED;
  const hasErrors = validation.errors.length > 0;
  const hasWarnings = validation.warnings.length > 0;

  // Calculate current state index for timeline
  const currentStateIndex = WORKFLOW_STATE_ORDER.indexOf(validation.current_state);
  
  // Calculate approval completion
  const approvalCount = approvalStatus ? 
    (approvalStatus.details_approved ? 1 : 0) +
    (approvalStatus.states_approved ? 1 : 0) +
    (approvalStatus.transitions_approved ? 1 : 0) +
    (approvalStatus.processes_approved ? 1 : 0) : 0;
  const approvalPercentage = approvalStatus ? (approvalCount / 4) * 100 : 0;

  // Determine which approvals are required for current state
  const getRequiredApprovals = (state: string): string[] => {
    switch (state) {
      case 'IDENTIFIED':
        return ['details_approved'];
      case 'DEFINED':
        return ['details_approved', 'states_approved'];
      case 'DESIGNED':
        return ['details_approved', 'states_approved', 'transitions_approved'];
      case 'IMPLEMENTED':
        return ['details_approved', 'states_approved', 'transitions_approved', 'processes_approved'];
      case 'VALIDATED':
        return ['details_approved', 'states_approved', 'transitions_approved', 'processes_approved'];
      default:
        return [];
    }
  };

  const requiredApprovals = getRequiredApprovals(validation.current_state);

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Workflow Status</span>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              loadValidation();
              loadApprovalStatus();
            }}
          />
        </div>
      }
    >
      {/* Workflow State Timeline */}
      <div style={{ marginBottom: 24 }}>
        <Steps
          current={currentStateIndex >= 0 ? currentStateIndex : 0}
          size="small"
          items={WORKFLOW_STATE_ORDER.map((state, index) => ({
            title: WORKFLOW_STATE_CONFIG[state]?.label || state,
            status: index < currentStateIndex ? 'finish' : 
                   index === currentStateIndex ? 'process' : 'wait',
            icon: index < currentStateIndex ? <CheckCircleOutlined /> : undefined,
          }))}
        />
      </div>

      {/* Current State */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge status={stateConfig.badge} text={stateConfig.label || validation.current_state} />
          <Tag color={stateConfig.badge}>{validation.current_state}</Tag>
        </div>
        <Progress
          percent={Math.round(validation.completion_percentage)}
          strokeColor={stateConfig.color}
          size="small"
          format={(percent) => `${percent}% Complete`}
        />
      </div>

      {/* Approval Status */}
      {approvalStatus && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Approval Status:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              { key: 'details_approved', label: 'Details', required: requiredApprovals.includes('details_approved') },
              { key: 'states_approved', label: 'States', required: requiredApprovals.includes('states_approved') },
              { key: 'transitions_approved', label: 'Transitions', required: requiredApprovals.includes('transitions_approved') },
              { key: 'processes_approved', label: 'Processes', required: requiredApprovals.includes('processes_approved') },
            ].map(({ key, label, required }) => {
              const isApproved = approvalStatus[key as keyof ApprovalStatus];
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isApproved ? (
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ClockCircleOutlined style={{ color: required ? '#ff4d4f' : '#d9d9d9' }} />
                  )}
                  <span style={{ flex: 1 }}>{label}</span>
                  {required && !isApproved && (
                    <Tag color="error">Required</Tag>
                  )}
                  {isApproved && (
                    <Tag color="success">Approved</Tag>
                  )}
                </div>
              );
            })}
          </div>
          <Progress
            percent={Math.round(approvalPercentage)}
            size="small"
            strokeColor="#52c41a"
            style={{ marginTop: 8 }}
            format={() => `${approvalCount}/4 Approvals`}
          />
        </div>
      )}

      {/* Approval Requirements (Blocking) */}
      {hasErrors && (
        <Alert
          message={
            <span>
              <strong>Approval Requirements</strong> (must be met to advance)
            </span>
          }
          description={
            <List
              size="small"
              dataSource={validation.errors}
              renderItem={(error) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                  {error}
                </List.Item>
              )}
            />
          }
          type="error"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Content Validation (Warnings/Recommendations) */}
      {hasWarnings && (
        <Alert
          message={
            <span>
              <strong>Content Recommendations</strong> (optional, can approve anyway)
            </span>
          }
          description={
            <List
              size="small"
              dataSource={validation.warnings}
              renderItem={(warning) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <ClockCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  {warning}
                </List.Item>
              )}
            />
          }
          type="warning"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Required Actions */}
      {validation.required_actions.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Next Steps:</div>
          <List
            size="small"
            dataSource={validation.required_actions}
            renderItem={(action) => (
              <List.Item style={{ padding: '4px 0' }}>
                <ArrowRightOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                {action}
              </List.Item>
            )}
          />
        </div>
      )}

      {/* Advance Button */}
      {validation.next_state && (
        <Button
          type="primary"
          block
          disabled={!validation.can_advance || advancing}
          loading={advancing}
          onClick={handleAdvanceWorkflow}
          icon={<CheckCircleOutlined />}
        >
          {validation.can_advance
            ? `Advance to ${WORKFLOW_STATE_CONFIG[validation.next_state]?.label || validation.next_state}`
            : `Complete requirements to advance`}
        </Button>
      )}

      {/* Validated State */}
      {validation.current_state === 'VALIDATED' && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
          <div style={{ marginTop: 8, fontWeight: 500 }}>Production Ready</div>
          <Tag color="success" style={{ marginTop: 8 }}>Template Ready</Tag>
        </div>
      )}
    </Card>
  );
};

